import { Attachment, AttachmentFile, notify } from '@billy/design-system'
import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router'

import { useInvalidateBills } from '@features/bills/hooks/useInvalidateBills'
import { useUpdateBill } from '@features/bills/hooks/useUpdateBill'
import {
  CreateBillPayload,
  CreateBillResponseData,
  GetBillResponseData,
  UpdateBillPayload,
  UpdateBillResponseData,
} from '@features/bills/query-api'
import { BillCreatedEventDetail } from '@features/bills/types/billCreatedEventDetail'
import { Contact } from '@features/contacts/types/contact'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useBillsEmberConnection } from '../../../../../contexts/billsEmberConnectionContext'
import { useDirtyRoute } from '../../../../../contexts/dirtyRouteContext'
import { CustomEvent } from '../../../../../enums/customEvent'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { useSegment } from '../../../../../hooks'
import { useOrganizationViewUrl } from '../../../../../hooks/routing/useOrganizationViewUrl'
import { APIError } from '../../../../../utils'
import { dispatchCustomEvent } from '../../../../../utils/customEvent'
import { getErrorMessage } from '../../../../../utils/getErrorMessage'
import { getOrganizationViewUrl } from '../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../utils/routing/reactRoute'
import { BillSubmitAction } from '../../../enums/billSubmitAction'
import { BulkMode } from '../../../enums/bulkMode'
import { useBulkAttachmentsNavigation } from '../../../hooks/useBulkAttachmentsNavigation'
import { useBulkBillsNavigation } from '../../../hooks/useBulkBillsNavigation'
import { decorateAttachmentsFilesWithAttachmentId } from '../utils/decorateAttachmentsFilesWithAttachmentId'
import { getParsedDataFromAttachment } from '../utils/getParsedDataFromAttachment'
import { isBillApproveAction } from '../utils/isBillApproveAction'
import { getBillDataTrackingStatuses } from '../utils/trackBillDataStatus'
import { useCreateBill } from './useCreateBill'

interface UseBillMutationsProps {
  billIdToCreateCreditNote?: string
  bulkEditMode?: BulkMode
  handleErrorManually?: (error: APIError | undefined) => boolean
}

export const useBillMutations = ({
  billIdToCreateCreditNote,
  bulkEditMode,
  handleErrorManually,
}: UseBillMutationsProps) => {
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const { track } = useSegment()
  const { t } = useTranslation()
  const { invalidateBill: invalidateEmberBill } = useBillsEmberConnection()
  const { hasNextBill, goToNextBill } = useBulkBillsNavigation()
  const { hasNextAttachment, goToNextAttachment } = useBulkAttachmentsNavigation()
  const { setDirty } = useDirtyRoute()
  const submitActionRef = useRef<BillSubmitAction>()
  const attachmentsFilesRef = useRef<AttachmentFile[]>()
  const vendorRef = useRef<Partial<Contact>>()
  const { invalidateBills } = useInvalidateBills()
  const { url: billsUrl } = useOrganizationViewUrl(reactRoute.organization.bills.getMainRoute())
  const { organization } = useUserOrganization()

  // Optimistic update of the query data so bill doesn't have to be refetched after creating or updating

  const updateBillQueryData = useCallback(
    (response: CreateBillResponseData | UpdateBillResponseData) => {
      const billId = response.bills[0].id
      const attachmentsFiles = decorateAttachmentsFilesWithAttachmentId(
        attachmentsFilesRef.current,
        response.attachments,
      )

      const previousData: GetBillResponseData | undefined = queryClient.getQueryData([QueryKeys.Bill, billId])
      const queryData: GetBillResponseData = {
        ...(previousData || {}),
        bill: {
          ...response.bills[0],
          attachments: attachmentsFiles?.map((file) => ({ file, id: file.attachmentId }) as Attachment) || [],
        },
        billLines: response.billLines || [],
      }

      queryClient.setQueryData([QueryKeys.Bill, billId], queryData)

      const isSomeAttachmentNewlyOwnered = attachmentsFilesRef.current?.find(
        (attachmentsFile) => !!attachmentsFile.attachmentId,
      )

      if (isSomeAttachmentNewlyOwnered) {
        queryClient.invalidateQueries(QueryKeys.AttachmentsList)
        queryClient.invalidateQueries(QueryKeys.AttachmentsCount)
      }

      queryClient.invalidateQueries(QueryKeys.Transactions)
      queryClient.invalidateQueries(QueryKeys.AccountStatements)
    },
    [queryClient],
  )

  const redirectAfterBulkAction = useCallback(() => {
    if (bulkEditMode === BulkMode.Bill) {
      if (hasNextBill) {
        goToNextBill()
      } else {
        navigate(`${billsUrl}${location.search}`, { replace: true })
      }
    }

    if (bulkEditMode === BulkMode.Attachment) {
      if (hasNextAttachment) {
        goToNextAttachment()
      } else {
        navigate(`${billsUrl}${location.search}`, { replace: true })
      }
    }
  }, [
    billsUrl,
    bulkEditMode,
    goToNextAttachment,
    goToNextBill,
    hasNextAttachment,
    hasNextBill,
    navigate,
    location.search,
  ])

  const handleCreateOrUpdateSuccess = useCallback(
    async (response: CreateBillResponseData | UpdateBillResponseData, isCreateAction?: boolean) => {
      const billId = response.bills[0].id
      const creditedBillId = response.bills[0].creditedBillId
      setDirty(false)
      invalidateBills()
      invalidateEmberBill(billId) // invalidating Ember side of the bill to reload transaction view & transaction attachments
      updateBillQueryData(response)
      await queryClient.invalidateQueries(QueryKeys.AttachmentsList)
      await queryClient.invalidateQueries(QueryKeys.NextVoucherNo)
      await queryClient.invalidateQueries(QueryKeys.VatSuggestions)
      await queryClient.invalidateQueries(QueryKeys.Bills)

      if (isBillApproveAction(submitActionRef.current)) {
        notify({
          id: 'bill-approve',
          message: t('bill.notification.approve_success'),
          variant: 'success',
        })

        if (response.bills[0].type === 'creditNote') {
          track('Bill Credited (Client)')
        } else {
          const bill = response.bills[0]
          const attachmentParsedData = getParsedDataFromAttachment(attachmentsFilesRef.current?.[0])
          const billWithVendor = bill ? { ...bill, vendor: vendorRef.current } : undefined

          track('Bill Approved (Client)', {
            context:
              submitActionRef.current === BillSubmitAction.Approve ||
              submitActionRef.current === BillSubmitAction.ApproveAndClose
                ? 'single'
                : 'bulk',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            prefilling_data_source: attachmentParsedData?.source ?? 'none',
            ...getBillDataTrackingStatuses(billWithVendor, attachmentParsedData),
          })
        }

        if (submitActionRef.current === BillSubmitAction.Approve) {
          if (billIdToCreateCreditNote) {
            await queryClient.invalidateQueries([QueryKeys.Bill, billIdToCreateCreditNote])
          }
          if (creditedBillId) {
            await queryClient.invalidateQueries([QueryKeys.Bill, creditedBillId])
          }
          await queryClient.invalidateQueries([QueryKeys.Bill, billId])
          navigate(
            `${getOrganizationViewUrl(organization?.url, reactRoute.organization.bills.getViewBillRoute(billId))}${
              location.search
            }`,
            { replace: true },
          )
        } else if (submitActionRef.current === BillSubmitAction.ApproveBulk) {
          redirectAfterBulkAction()
        } else if (submitActionRef.current === BillSubmitAction.ApproveAndClose) {
          dispatchCustomEvent<BillCreatedEventDetail>(CustomEvent.BillCreated, { billId })
        }
      } else if (submitActionRef.current === BillSubmitAction.Save) {
        notify({
          id: 'bill-save-as-draft',
          message: t('bill.notification.save_success'),
          variant: 'success',
        })

        if (isCreateAction) {
          navigate(
            `${getOrganizationViewUrl(organization?.url, reactRoute.organization.bills.getEditBillRoute(billId))}${
              location.search
            }`,
            { replace: true },
          )
        }

        track('Bill Draft Created (Client)', {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          has_attachment: !!response.attachments,
        })
      }
    },
    [
      billIdToCreateCreditNote,
      navigate,
      invalidateBills,
      invalidateEmberBill,
      location.search,
      organization?.url,
      queryClient,
      redirectAfterBulkAction,
      setDirty,
      t,
      track,
      updateBillQueryData,
    ],
  )

  // Creating new bill

  const { create, isLoading: isCreateProcessing } = useCreateBill({
    onSuccess: (response) => handleCreateOrUpdateSuccess(response, true),
    onError: (error) => {
      const isErrorHandled = handleErrorManually?.(error)

      if (isErrorHandled) {
        return
      }

      const message = getErrorMessage(error, 'bill')

      if (isBillApproveAction(submitActionRef.current)) {
        notify({
          id: 'bill-approve',
          message: message || t('bill.notification.approve_error'),
          variant: 'error',
        })
      } else if (submitActionRef.current === BillSubmitAction.Save) {
        notify({
          id: 'bill-save-as-draft',
          message: message || t('bill.notification.save_error'),
          variant: 'error',
        })
      }
    },
    onSettled: () => {
      submitActionRef.current = undefined
      attachmentsFilesRef.current = undefined
      vendorRef.current = undefined
    },
  })

  // Updating existing bill

  const { update, isLoading: isUpdateProcessing } = useUpdateBill({
    onSuccess: (response) => handleCreateOrUpdateSuccess(response, false),
    onError: (error) => {
      const isErrorHandled = handleErrorManually?.(error)

      if (isErrorHandled) {
        return
      }

      const message = getErrorMessage(error, 'bill')

      notify({
        id: 'bill-updated',
        message: message || t('bill.notification.update_error'),
        variant: 'error',
      })
    },
    onSettled: () => {
      submitActionRef.current = undefined
      attachmentsFilesRef.current = undefined
      vendorRef.current = undefined
    },
  })

  const createBill = useCallback(
    (
      payload: CreateBillPayload,
      submitAction: BillSubmitAction,
      attachmentsFiles: AttachmentFile[] | undefined,
      vendor?: Partial<Contact>,
    ) => {
      submitActionRef.current = submitAction
      attachmentsFilesRef.current = attachmentsFiles
      vendorRef.current = vendor
      create(payload)
    },
    [create],
  )

  const updateBill = useCallback(
    (
      payload: UpdateBillPayload,
      submitAction: BillSubmitAction,
      attachmentsFiles?: AttachmentFile[] | undefined,
      vendor?: Partial<Contact>,
    ) => {
      submitActionRef.current = submitAction
      attachmentsFilesRef.current = attachmentsFiles
      vendorRef.current = vendor
      update(payload)
    },
    [update],
  )

  const processingAction = isCreateProcessing || isUpdateProcessing ? submitActionRef.current : undefined

  return {
    createBill,
    isCreateProcessing,
    isUpdateProcessing,
    processingAction,
    updateBill,
  }
}
